
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar, Clock, User, Search, Filter, FileText, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO, isToday, isThisWeek, isThisMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import AppointmentManagement from './AppointmentManagement';
import { Appointment as AppointmentType } from '@/types/dashboard';

interface Appointment {
  id: string;
  date: string;
  time: string;
  status: string;
  patient_info: {
    first_name: string;
    last_name: string;
    email: string;
  };
  duration: number;
  meet_link?: string;
  psychologist_id?: string;
}

interface AppointmentListProps {
  psychologistId?: string;
  selectedDate?: Date;
  onViewNotes?: (appointmentId: string) => void;
}

const AppointmentList: React.FC<AppointmentListProps> = ({ 
  psychologistId,
  selectedDate,
  onViewNotes
}) => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [filteredAppointments, setFilteredAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<string>('all');

  useEffect(() => {
    if (!psychologistId) {
      toast.error("ID do psicólogo não encontrado");
      setLoading(false);
      return;
    }

    async function loadAppointments() {
      try {
        setLoading(true);
        
        let query = supabase
          .from("appointments")
          .select("id, date, time, status, duration, meet_link, user_id, psychologist_id")
          .eq("psychologist_id", psychologistId)
          .order("date", { ascending: true })
          .order("time", { ascending: true });
        
        // Se uma data específica foi selecionada, filtrar por ela
        if (selectedDate) {
          const formattedDate = format(selectedDate, 'yyyy-MM-dd');
          query = query.eq("date", formattedDate);
        } else {
          // Se não, mostrar apenas consultas futuras
          const today = format(new Date(), 'yyyy-MM-dd');
          query = query.gte("date", today);
        }

        const { data: appointmentsData, error } = await query;

        if (error) {
          console.error("Erro ao carregar consultas:", error);
          toast.error("Erro ao carregar lista de consultas");
          return;
        }

        // Buscar dados dos perfis dos pacientes
        const processedAppointments: Appointment[] = [];
        
        for (const appointment of appointmentsData || []) {
          let patientInfo = {
            first_name: "Cliente",
            last_name: "",
            email: "email@example.com"
          };

          if (appointment.user_id) {
            const { data: profileData } = await supabase
              .from("profiles")
              .select("first_name, last_name, email")
              .eq("id", appointment.user_id)
              .single();

            if (profileData) {
              patientInfo = {
                first_name: profileData.first_name || "Cliente",
                last_name: profileData.last_name || "",
                email: profileData.email || "email@example.com"
              };
            }
          }

          processedAppointments.push({
            ...appointment,
            patient_info: patientInfo
          });
        }

        setAppointments(processedAppointments);
        setFilteredAppointments(processedAppointments);
      } catch (error) {
        console.error("Erro inesperado:", error);
        toast.error("Erro inesperado ao carregar consultas");
      } finally {
        setLoading(false);
      }
    }

    loadAppointments();
  }, [psychologistId, selectedDate]);

  // Aplicar filtros quando mudarem
  useEffect(() => {
    let result = [...appointments];
    
    // Aplicar filtro de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(appointment => 
        appointment.patient_info.first_name.toLowerCase().includes(term) ||
        appointment.patient_info.last_name.toLowerCase().includes(term) ||
        appointment.patient_info.email.toLowerCase().includes(term)
      );
    }
    
    // Aplicar filtro de status
    if (statusFilter !== 'all') {
      result = result.filter(appointment => appointment.status === statusFilter);
    }
    
    // Aplicar filtro de tempo
    if (timeFilter !== 'all' && !selectedDate) {
      const today = new Date();
      result = result.filter(appointment => {
        const appointmentDate = parseISO(appointment.date);
        
        switch (timeFilter) {
          case 'today':
            return isToday(appointmentDate);
          case 'week':
            return isThisWeek(appointmentDate);
          case 'month':
            return isThisMonth(appointmentDate);
          default:
            return true;
        }
      });
    }
    
    setFilteredAppointments(result);
  }, [appointments, searchTerm, statusFilter, timeFilter, selectedDate]);

  const formatAppointmentDate = (date: string) => {
    try {
      return format(parseISO(date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    } catch (e) {
      return "Data inválida";
    }
  };

  const formatAppointmentTime = (time: string) => {
    try {
      // Convert from 24-hour format "14:30:00" to "14:30"
      return time.substring(0, 5);
    } catch (e) {
      return "Hora inválida";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "confirmed":
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Confirmada</Badge>;
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-200">Pendente</Badge>;
      case "cancelled":
        return <Badge variant="destructive">Cancelada</Badge>;
      case "completed":
        return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">Realizada</Badge>;
      case "no_show":
        return <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">Não Compareceu</Badge>;
      case "pending_reschedule":
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 border-blue-200">Aguardando Reagendamento</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const joinMeeting = (meetLink: string) => {
    window.open(meetLink, '_blank');
  };

  const handleViewNotes = (appointmentId: string) => {
    if (onViewNotes) {
      onViewNotes(appointmentId);
    }
  };

  const handleAppointmentUpdated = (updatedAppointment: AppointmentType) => {
    // Atualizar a lista de consultas com a consulta atualizada
    setAppointments(appointments.map(appointment => 
      appointment.id === updatedAppointment.id 
        ? { ...appointment, status: updatedAppointment.status }
        : appointment
    ));
  };

  // Converter appointment para o formato esperado pelo AppointmentManagement
  const convertToAppointmentType = (appointment: Appointment): AppointmentType => {
    return {
      id: appointment.id,
      patientId: appointment.patient_info.first_name + ' ' + appointment.patient_info.last_name,
      patientName: appointment.patient_info.first_name + ' ' + appointment.patient_info.last_name,
      psychologistId: appointment.psychologist_id || '',
      date: appointment.date,
      time: appointment.time,
      duration: appointment.duration,
      status: appointment.status as any,
      paymentStatus: 'paid' as any,
      createdAt: '',
      updatedAt: '',
      scheduledAt: `${appointment.date}T${appointment.time}`
    };
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Lista de Consultas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-5 w-24" />
                </div>
                <div className="mt-3 space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-lg">
            {selectedDate 
              ? `Consultas em ${format(selectedDate, "dd 'de' MMMM", { locale: ptBR })}`
              : "Próximas Consultas"
            }
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar paciente..."
                className="pl-8 h-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[130px] h-9">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="confirmed">Confirmadas</SelectItem>
                  <SelectItem value="pending">Pendentes</SelectItem>
                  <SelectItem value="completed">Realizadas</SelectItem>
                  <SelectItem value="cancelled">Canceladas</SelectItem>
                  <SelectItem value="no_show">Não Compareceu</SelectItem>
                  <SelectItem value="pending_reschedule">Aguardando Reagendamento</SelectItem>
                </SelectContent>
              </Select>
              
              {!selectedDate && (
                <Select value={timeFilter} onValueChange={setTimeFilter}>
                  <SelectTrigger className="w-[130px] h-9">
                    <Clock className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="today">Hoje</SelectItem>
                    <SelectItem value="week">Esta Semana</SelectItem>
                    <SelectItem value="month">Este Mês</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filteredAppointments.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">
              {searchTerm || statusFilter !== 'all' || timeFilter !== 'all'
                ? "Nenhuma consulta encontrada com os filtros aplicados."
                : selectedDate
                  ? "Não há consultas agendadas para esta data."
                  : "Não há consultas agendadas no momento."
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAppointments.map((appointment) => (
              <div key={appointment.id} className="border rounded-lg p-4 hover:border-primary transition-colors">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">
                        {appointment.patient_info.first_name} {appointment.patient_info.last_name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {appointment.patient_info.email}
                      </p>
                    </div>
                  </div>
                  <div>
                    {getStatusBadge(appointment.status)}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="text-sm">{formatAppointmentDate(appointment.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
                    <span className="text-sm">
                      {formatAppointmentTime(appointment.time)} ({appointment.duration} minutos)
                    </span>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewNotes(appointment.id)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Prontuário
                    </Button>
                    
                    {appointment.meet_link && appointment.status === 'confirmed' && (
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => joinMeeting(appointment.meet_link || '')}
                      >
                        <Video className="h-4 w-4 mr-2" />
                        Atender
                      </Button>
                    )}
                  </div>
                </div>

                {/* Sistema de gerenciamento de consultas */}
                <div className="border-t pt-3">
                  <AppointmentManagement
                    appointment={convertToAppointmentType(appointment)}
                    userRole="psychologist"
                    onAppointmentUpdated={handleAppointmentUpdated}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AppointmentList;
